import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Switch, Row, Col, DatePicker, Flex, Select, Space, Button, Upload, Avatar, message, Popconfirm } from 'antd';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getProducts, deleteUserProfilePicture } from '../UsersServices';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import ImgCrop from 'antd-img-crop';
import { uploadFile } from '../../Messages/MessagesServices';
import { resolveBackendMessage, getBackendMessageOr } from '../../../Utils/apiMessages';
import { t } from 'i18next';
import { DeleteOutlined, EditOutlined, CameraOutlined } from '@ant-design/icons';
import { getFileUploadPolicy, validateFileAgainstPolicy, formatBytes } from '../../../Utils/fileUploadPolicy';
import { LANGUAGE_OPTIONS } from '../../../Constants/language';
import { shouldUseNativePicker } from '../../../Utils/nativeFileUpload';
import { setupNativeUploadIntegration } from '../../../Utils/nativeUploadIntegration';

dayjs.extend(utc);

const timezones = moment.tz.names().map((timezone) => ({
    value: timezone,
    label: timezone,
}));

// We rely on backend-provided timezone_name for display

const UserForm = ({ form, initialValues, screens, onFinish, onValuesChange, setSubmitLoading: setParentSubmitLoading }) => {
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const { setLoading } = useGlobal();
    const [products, setProducts] = useState([]);
    const { handleError } = useErrorHandler();
    const [photoUrl, setPhotoUrl] = useState(initialValues?.picture_url || undefined);
    const [photoUploadId, setPhotoUploadId] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const [filePolicy, setFilePolicy] = useState(null);
    const isNative = shouldUseNativePicker();

    // Setup native upload integration for camera on mobile
    useEffect(() => {
        if (isNative) {
            const cleanup = setupNativeUploadIntegration();
            return cleanup;
        }
    }, [isNative]);

    // Pre-fetch file upload policy when modal opens
    useEffect(() => {
        getFileUploadPolicy()
            .then(policy => setFilePolicy(policy))
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (initialValues) {
            // Prefer backend's timezone_name; also convert discount_expiry
            const { loyalty_level, password, ...rest } = initialValues;
            const transformedValues = {
                ...rest,
                timezone: initialValues.timezone_name || undefined,
                discount_expiry: initialValues.discount_expiry ? dayjs(initialValues.discount_expiry) : undefined,
                language: (initialValues.language || initialValues.locale || '')?.split(/[-_]/)[0] || undefined,
            };
            form.setFieldsValue(transformedValues);
            setPhotoUrl(initialValues?.picture_url || undefined);
        } else {
            form.resetFields();
            setPhotoUrl(undefined);
            setPhotoUploadId(null);
        }
    }, [initialValues, form]);

    // Set loyalty_level only after products have loaded so the Select shows the label, not the raw value
    useEffect(() => {
        if (products.length > 0 && initialValues?.loyalty_level !== undefined) {
            form.setFieldsValue({ loyalty_level: initialValues.loyalty_level });
        }
    }, [products, initialValues, form]);


    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            const language = values.language;
            await onFinish({
                ...values,
                language,
                profile_picture_file_upload_id: photoUploadId || undefined,
            }, form);
        } finally {
            setSubmitLoading(false);
            if (setParentSubmitLoading) setParentSubmitLoading(false);
        }
    };

    const getProductsCall = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await getProducts();
            setProducts(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError, setLoading]);


    useEffect(() => {
        getProductsCall();
    }, [getProductsCall]);
    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={initialValues ? (() => { const { loyalty_level, ...rest } = initialValues; return rest; })() : {}}
            className={screens.xs ? 'px-2' : 'px-4'}
            style={{ padding: screens.xs ? '0 8px' : '0 16px', overflowX: 'hidden' }}
            onValuesChange={onValuesChange}
            onFinish={handleSubmit}
            onFinishFailed={() => { if (setParentSubmitLoading) setParentSubmitLoading(false); }}
        >
            <Row style={{ marginBottom: 12 }}>
                <Col span={24}>
                    <Space align="center" size={16}>
                        <Avatar size={64} src={photoUrl} data-file-upload-id={photoUploadId || undefined}>
                            {(initialValues?.first_name?.[0] || '') + (initialValues?.last_name?.[0] || '')}
                        </Avatar>
                        <ImgCrop rotationSlider zoomSlider aspect={1} quality={0.9} modalTitle={t('edit_photo')} shape="round">
                            <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={async (file) => {
                                    try {
                                        const policy = filePolicy || await getFileUploadPolicy();
                                        const validation = validateFileAgainstPolicy(file, policy);
                                        if (!validation.valid) {
                                            if (validation.code === 'extension') {
                                                const allowed = (validation.details?.allowed || []).join(', ');
                                                message.error(t('file_type_not_allowed', { exts: allowed }));
                                            } else if (validation.code === 'size') {
                                                const max = formatBytes(validation.details?.maxSize);
                                                message.error(t('file_too_large_max', { max }));
                                            } else if (validation.code === 'name') {
                                                const max = validation.details?.maxName;
                                                message.error(t('filename_too_long_max', { max }));
                                            }
                                            return false;
                                        }
                                        setUploadingPhoto(true);
                                        const response = await uploadFile(file);
                                        const fileData = response?.data || response;
                                        const url = fileData?.file_url;
                                        if (url) {
                                            setPhotoUrl(url);
                                            setPhotoUploadId(fileData?.id || null);
                                            // Use backend message_code if available
                                            const successMessage = getBackendMessageOr(t, response, 'upload_success');
                                            if (successMessage) {
                                                message.success(successMessage);
                                            }
                                        }
                                    } catch (e) {
                                        // Use handleError for API errors with message_code support
                                        handleError(e);
                                    } finally {
                                        setUploadingPhoto(false);
                                    }
                                    return false;
                                }}
                            >
                                <Button
                                    shape="circle"
                                    icon={isNative || !photoUrl ? <CameraOutlined /> : <EditOutlined />}
                                    loading={uploadingPhoto}
                                    aria-label={t('change_photo')}
                                />
                            </Upload>
                        </ImgCrop>
                        {photoUrl && initialValues?.id && (
                            <Popconfirm
                                title={t('delete_profile_picture')}
                                okText={t('yes')}
                                cancelText={t('no')}
                                onConfirm={async () => {
                                    try {
                                        setDeletingPhoto(true);
                                        const payload = await deleteUserProfilePicture(initialValues.id);
                                        setPhotoUrl(undefined);
                                        setPhotoUploadId(null);
                                        {
                                            const text = resolveBackendMessage(t, payload);
                                            if (text) message.success(text);
                                        }
                                    } catch (e) {
                                        handleError(e);
                                        message.error(t('failed_to_delete_profile_picture'));
                                    } finally {
                                        setDeletingPhoto(false);
                                    }
                                }}
                            >
                                <Button danger shape="circle" icon={<DeleteOutlined />} loading={deletingPhoto} aria-label={t('delete_profile_picture')} />
                            </Popconfirm>
                        )}
                    </Space>
                </Col>
            </Row>
            <Row gutter={screens.xs ? 8 : 16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="first_name"
                        label={t('first_name')}
                        rules={[{ required: true, message: t('please_input_the_first_name') }]}
                        style={{ marginBottom: 12 }}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="last_name"
                        label={t('last_name')}
                        rules={[{ required: true, message: t('please_input_the_last_name') }]}
                        style={{ marginBottom: 12 }}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="email"
                        label={t('email')}
                        rules={[
                            { required: true, message: t('please_input_the_email') },
                            { type: 'email', message: t('please_enter_a_valid_email_address') },
                        ]}
                        style={{ marginBottom: 12 }}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="gender"
                        label={t('gender')}
                        rules={[
                            { message: t('please_select_gender') },
                        ]}
                        style={{ marginBottom: 12 }}
                    >
                        <Select
                            options={[
                                { value: 'male', label: t('male') },
                                { value: 'female', label: t('female') },
                            ]}
                            showSearch={false}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="discount_percentage"
                        label={t('discount_percentage')}
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.resolve();
                                    }
                                    const num = Number(value);
                                    if (Number.isNaN(num) || num < 0 || num > 80) {
                                        return Promise.reject(new Error(t('discount_must_be_between_0_and_80')));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                        style={{ marginBottom: 12 }}
                    >
                        <Input type="number" min={0} max={80} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('discount_expiry')}
                        name="discount_expiry"
                        style={{ marginBottom: 12 }}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                            showTime={false}
                            utc
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('language')}
                        name="language"
                        style={{ width: '100%', marginBottom: 12 }}
                    >
                        <Select
                            options={LANGUAGE_OPTIONS}
                            placeholder={t('language')}
                            showSearch={false}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('timezone')}
                        name="timezone"
                        style={{ width: '100%', marginBottom: 12 }}
                    >
                        <Select
                            options={timezones}
                            placeholder={t('select_timezone')}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="password"
                        label={t('password')}
                        rules={[
                            { required: !initialValues, message: t('please_input_your_password') },
                            { min: 8, message: t('password_must_be_at_least_8_characters') },
                        ]}
                        style={{ marginBottom: 12 }}
                    >
                        <Input.Password placeholder={initialValues ? '••••••••' : ''} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="loyalty_level"
                        label={t('select_plan')}
                        style={{ marginBottom: 12 }}
                    >
                        <Select loading={products.length === 0} notFoundContent={products.length === 0 ? t('loading') : undefined}>
                            {products.map(product => <Select.Option key={product.id} value={product.loyalty_level}>{`${product.title} (${product.coins} coins)`}</Select.Option>)}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Flex gap={4}>
                        <Form.Item style={{ flexGrow: 1, marginBottom: 0 }} name="is_disabled" label={t('disabled_account')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item style={{ flexGrow: 1, marginBottom: 0 }} name="is_admin" label={t('admin_access')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item style={{ flexGrow: 1, marginBottom: 0 }} name="is_verified" label={t('verified_account')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Flex>
                </Col>
            </Row>
        </Form>
    );
};

export default UserForm;
